// Hexcrawl - Hex Grid Dungeon Map Editor
// Main entry point

import type { Hex, HexLayout, Point, Stroke, Room, Corridor } from "./types.ts";
import { LayerManager } from "./canvas/layers.ts";
import { HexRenderer } from "./canvas/renderer.ts";
import { PointerHandler } from "./input/pointer.ts";
import { hexToKey, keyToHex, hexEqual, pixelToHex, hexToPixel, hexDistance } from "./hex/coordinates.ts";
import {
  classifyStroke,
  classifyStrokeDetailed,
  autoCloseStroke,
  getCloseness,
  isClosed,
  getStrokeMetrics,
} from "./input/gestures.ts";
import { strokeToHexRegion } from "./input/interpreter.ts";
import { DungeonModel } from "./model/dungeon.ts";
import { SelectionManager, selectAtHex } from "./ui/selection.ts";
import { findCorridorPath, recalculateCorridorPath, recalculateCorridorPathThroughRooms, getEdgeHexes, calculateRadialExpansion, calculateRadialContraction } from "./hex/algorithms.ts";
import { AnnotationOverlay } from "./ui/annotations.ts";
import { Toolbar } from "./ui/toolbar.ts";
import {
  StrokeFeedbackOverlay,
  analyzeStrokeInProgress,
} from "./ui/stroke-feedback.ts";
import {
  suggestTemplate,
  generateRectangle,
  generateCircle,
  snapToRectangle,
  analyzeShape,
} from "./input/shape-relax.ts";
import { calculateDesires, type RoomDesire } from "./ui/desires.ts";
import { Minimap } from "./ui/minimap.ts";
import { KeyboardHandler } from "./input/keyboard.ts";
import { UndoTreeManager } from "./undo/manager.ts";
import { UndoTreePanel } from "./ui/undo-panel.ts";
import { serializeRoom, serializeCorridor } from "./undo/deltas.ts";
import type { OperationDelta } from "./undo/tree.ts";

/**
 * Main application class
 */
class HexcrawlApp {
  private container: HTMLElement;
  private layers: LayerManager;
  private renderer: HexRenderer;
  private pointer: PointerHandler;
  private layout: HexLayout;
  private dungeon: DungeonModel;
  private selection: SelectionManager;
  private annotations: AnnotationOverlay;
  private toolbar: Toolbar;
  private strokeFeedback: StrokeFeedbackOverlay;
  private minimap: Minimap;
  private keyboard: KeyboardHandler;
  private undoManager: UndoTreeManager;
  private undoTreePanel: UndoTreePanel;
  private animationFrameId: number | null = null;
  private animTime: number = 0;
  private roomDesires: Map<string, RoomDesire[]> = new Map();

  // Current hover state
  private hoveredHex: Hex | null = null;

  // Preview state while drawing
  private previewHexes: Set<string> | null = null;

  // Corridor creation state
  private corridorStartRoom: Room | null = null;

  // Last stroke end position (for feedback messages)
  private lastStrokeEndPosition: Point = { x: 0, y: 0 };

  // Quick shape mode state
  private quickShapeStart: Hex | null = null;
  private quickShapeType: "rectangle" | "circle" | null = null;

  // Quick carve mode state (shift/alt + right-click for carving shapes from selected room)
  private quickCarveStart: Hex | null = null;
  private quickCarveType: "rectangle" | "circle" | null = null;

  // Room drag state (supports multi-room dragging)
  private draggingRooms: Room[] = [];
  private dragStartHex: Hex | null = null;
  private dragCurrentHex: Hex | null = null;
  private wasRoomDrag: boolean = false;

  // Room scaling preview state (Ctrl+scroll)
  private scalingPreviewActive: boolean = false;
  private scalingOriginalRooms: Map<string, ReturnType<typeof serializeRoom>> = new Map();
  private scalingOriginalCorridors: ReturnType<typeof serializeCorridor>[] = [];
  private scalingCenter: Point | null = null;

  constructor(containerId: string) {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container element not found: ${containerId}`);
    }
    this.container = container;

    // Initialize layout - center origin in viewport
    const { width, height } = this.getContainerSize();
    this.layout = {
      size: 30,
      origin: { x: width / 2, y: height / 2 },
      spacing: 0,
    };

    // Initialize canvas layers
    this.layers = new LayerManager(this.container);

    // Initialize renderer with the static layer canvas
    const staticLayer = this.layers.getLayer("static");
    if (!staticLayer) {
      throw new Error("Static layer not found");
    }
    this.renderer = new HexRenderer(staticLayer.canvas);

    // Initialize pointer handler with the UI layer
    const uiLayer = this.layers.getLayer("ui");
    if (!uiLayer) {
      throw new Error("UI layer not found");
    }
    this.pointer = new PointerHandler(uiLayer.canvas, this.layout);

    // Initialize dungeon model
    this.dungeon = new DungeonModel();

    // Initialize selection manager
    this.selection = new SelectionManager();

    // Initialize annotation overlay
    this.annotations = new AnnotationOverlay(this.container);
    this.annotations.onConfirm((roomId, label) => {
      this.handleAnnotationConfirm(roomId, label);
    });

    // Initialize toolbar
    this.toolbar = new Toolbar(this.container);

    // Initialize stroke feedback overlay
    this.strokeFeedback = new StrokeFeedbackOverlay(this.container);

    // Initialize minimap
    this.minimap = new Minimap(this.container);
    this.minimap.setOnNavigate((worldX, worldY) => {
      this.navigateToWorldPosition(worldX, worldY);
    });

    // Initialize keyboard handler
    this.keyboard = new KeyboardHandler();

    // Initialize undo manager
    this.undoManager = new UndoTreeManager(this.dungeon);
    this.undoManager.onStateChange(() => {
      this.renderRooms();
      this.updateDesires();
      this.selection.clear();
      this.pointer.setSubdivisionMode(false);
    });

    // Initialize undo tree panel
    this.undoTreePanel = new UndoTreePanel(this.container);
    this.undoTreePanel.onNavigate((nodeId) => {
      this.undoManager.navigateTo(nodeId);
    });
    this.undoTreePanel.onHover((nodeId) => {
      if (nodeId) {
        // Compute diff and show preview in minimap
        const diff = this.undoManager.computeDiffToNode(nodeId);
        if (diff) {
          this.minimap.setDiffState(diff, this.layout);
          this.updateMinimap();
        }
      } else {
        // Clear diff preview
        this.minimap.clearDiff();
        this.updateMinimap();
      }
    });
    this.undoManager.onTreeChange((tree) => {
      this.undoTreePanel.update(tree);
    });
    // Initialize panel with current tree state
    this.undoTreePanel.update(this.undoManager.getTree());

    // Set up event handlers
    this.setupEventHandlers();

    // Register keyboard shortcuts
    this.setupKeyboardShortcuts();

    // Initial render
    this.renderGrid();

    // Start animation loop
    this.startAnimationLoop();

    console.log("Hexcrawl initialized!");
  }

  private getContainerSize(): { width: number; height: number } {
    return {
      width: this.container.clientWidth,
      height: this.container.clientHeight,
    };
  }

  /**
   * Start the animation loop for desire visualization
   */
  private startAnimationLoop(): void {
    const animate = (timestamp: number) => {
      this.animTime = timestamp;
      this.renderDesireLayer();
      this.animationFrameId = requestAnimationFrame(animate);
    };
    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Render the desire layer with animated glows
   */
  private renderDesireLayer(): void {
    const previewLayer = this.layers.getLayer("preview");
    if (!previewLayer) return;

    // Only render desires when not actively drawing
    const strokePoints = this.pointer.getCurrentStroke();
    if (strokePoints.length > 0) return;

    // Don't clear preview during quick shape mode
    if (this.quickShapeStart && this.quickShapeType) return;

    // Don't clear preview during quick carve mode
    if (this.quickCarveStart && this.quickCarveType) return;

    // Don't clear preview during subdivision stroke
    const subdivisionStroke = this.pointer.getSubdivisionStroke();
    if (subdivisionStroke.length > 0) return;

    const ctx = previewLayer.ctx;

    // Clear the preview layer first
    this.layers.clearLayer("preview");

    // Render potential connection lines first (lowest layer)
    const rooms = Array.from(this.dungeon.rooms.values());
    this.renderer.renderPotentialConnections(
      ctx,
      rooms,
      this.dungeon.corridors,
      this.layout,
      this.animTime
    );

    // Render desire glows and chevrons for rooms with unmet desires
    for (const [roomId, desires] of this.roomDesires) {
      const room = this.dungeon.rooms.get(roomId);
      if (!room) continue;

      for (const desire of desires) {
        if (desire.type === 'needs-label') {
          this.renderer.renderDesireGlow(
            ctx,
            room,
            this.layout,
            desire.type,
            this.animTime,
            desire.strength
          );
        } else if (desire.type === 'wants-connection') {
          // Find connected room center if any
          let connectedCenter: Point | null = null;
          for (const corridor of this.dungeon.corridors.values()) {
            if (corridor.connects.includes(roomId)) {
              const otherRoomId = corridor.connects.find(id => id !== roomId);
              if (otherRoomId) {
                const otherRoom = this.dungeon.rooms.get(otherRoomId);
                if (otherRoom) {
                  connectedCenter = this.getRoomCenterFromHexes(otherRoom.hexes);
                  break;
                }
              }
            }
          }

          this.renderer.renderConnectionChevron(
            ctx,
            room,
            this.layout,
            connectedCenter,
            this.animTime,
            desire.strength
          );
        }
      }
    }

    // Render selection highlight for all selected rooms
    const selectedRooms = this.selection.getSelectedRooms();
    const selectedCorridorId = this.selection.getSelectedCorridor();

    if (selectedRooms.size > 0) {
      for (const roomId of selectedRooms) {
        const room = this.dungeon.rooms.get(roomId);
        if (room) {
          this.renderer.renderSelectionOutline(
            ctx,
            room.hexes,
            this.layout
          );
        }
      }
    } else if (selectedCorridorId) {
      const corridor = this.dungeon.corridors.get(selectedCorridorId);
      if (corridor) {
        this.renderer.renderSelectionOutline(
          ctx,
          corridor.hexes,
          this.layout
        );
      }
    }

    // Render hover highlight on top of desires
    if (this.hoveredHex) {
      this.renderer.renderHexHighlight(
        ctx,
        this.hoveredHex,
        this.layout,
        "rgba(255, 200, 100, 0.3)"
      );
    }
  }

  /**
   * Update desires when dungeon changes
   */
  private updateDesires(): void {
    this.roomDesires = calculateDesires(this.dungeon.rooms, this.dungeon.corridors);
  }

  /**
   * Animate a satisfying "snap" effect when a shape closes into hexes
   */
  private animateSnapEffect(hexes: Set<string>, onComplete: () => void): void {
    const previewLayer = this.layers.getLayer("preview");
    if (!previewLayer) {
      onComplete();
      return;
    }

    const startTime = performance.now();
    const duration = 250; // 250ms animation

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(1, elapsed / duration);

      // Eased progress (ease-out cubic)
      const eased = 1 - Math.pow(1 - progress, 3);

      // Clear and redraw preview layer
      this.layers.clearLayer("preview");
      const ctx = previewLayer.ctx;

      // Phase 1 (0-0.6): Fade in the hex region
      // Phase 2 (0.6-1.0): Scale bounce effect

      if (progress < 0.6) {
        // Fade in
        const fadeProgress = progress / 0.6;
        const alpha = fadeProgress * 0.4;
        this.renderer.renderHexesPreview(ctx, hexes, this.layout, `rgba(100, 180, 100, ${alpha})`);
      } else {
        // Bounce effect
        const bounceProgress = (progress - 0.6) / 0.4;
        const scale = 1 + Math.sin(bounceProgress * Math.PI) * 0.08;

        ctx.save();

        // Calculate center of hex region for scaling
        let sumX = 0, sumY = 0, count = 0;
        for (const key of hexes) {
          const [q, r] = key.split(",").map(Number);
          const center = hexToPixel({ q, r }, this.layout);
          sumX += center.x;
          sumY += center.y;
          count++;
        }
        const centerX = sumX / count;
        const centerY = sumY / count;

        ctx.translate(centerX, centerY);
        ctx.scale(scale, scale);
        ctx.translate(-centerX, -centerY);

        this.renderer.renderHexesPreview(ctx, hexes, this.layout, "rgba(100, 180, 100, 0.5)");

        ctx.restore();
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.layers.clearLayer("preview");
        onComplete();
      }
    };

    requestAnimationFrame(animate);
  }

  private setupEventHandlers(): void {
    // Listen for Ctrl release to commit scaling preview
    window.addEventListener("keyup", (e) => {
      if (e.key === "Control" && this.scalingPreviewActive) {
        this.commitScalingPreview();
      }
    });

    // Also commit if window loses focus while scaling
    window.addEventListener("blur", () => {
      if (this.scalingPreviewActive) {
        this.commitScalingPreview();
      }
    });

    // Hover highlighting
    this.pointer.on("hover", ({ hex }) => {
      if (hex) {
        if (!this.hoveredHex || !hexEqual(hex, this.hoveredHex)) {
          this.hoveredHex = hex;
          this.renderHoverHighlight();
        }
      } else {
        // Clear hover when mouse moves to invalid position
        this.hoveredHex = null;
      }
    });

    // Handle pointer down for quick shape mode or room dragging
    this.pointer.on("down", ({ hex, shiftKey, ctrlKey, altKey, button }) => {
      // Clear hover highlight when starting to draw
      this.hoveredHex = null;

      // Quick carve mode (ctrl/alt + right-click when room is selected)
      // Note: shift+right-click is reserved by browsers to force-show context menu
      if (hex && button === 2 && (ctrlKey || altKey) && this.selection.getSelectedRoom()) {
        console.log("Starting quick carve mode:", ctrlKey ? "rectangle" : "circle", "at hex", hex);
        this.quickCarveStart = hex;
        this.quickCarveType = ctrlKey ? "rectangle" : "circle";
        return;
      }

      console.log("Down event - button:", button, "ctrl:", ctrlKey, "alt:", altKey, "selectedRoom:", this.selection.getSelectedRoom());

      if (hex && (ctrlKey || altKey)) {
        // Start quick shape mode (left-click)
        // ctrl = rectangle, alt = circle
        this.quickShapeStart = hex;
        this.quickShapeType = ctrlKey ? "rectangle" : "circle";
      } else if (hex && !ctrlKey && !altKey) {
        // Check if clicking on a room (no modifiers = potential drag)
        const key = hexToKey(hex);
        const room = this.dungeon.getRoom(key);
        if (room) {
          // Only start drag if clicking on interior (non-edge) hex
          // Edge hexes allow corridor drawing
          const edgeHexes = getEdgeHexes(room.hexes);
          const isEdge = edgeHexes.has(key);
          const hasInterior = edgeHexes.size < room.hexes.size;

          // Start drag if: clicking interior, OR room has no interior (all edge)
          if (!isEdge || !hasInterior) {
            // Check if this room is part of a multi-selection
            if (this.selection.isRoomSelected(room.id) && this.selection.hasMultipleRoomsSelected()) {
              // Drag all selected rooms together
              this.draggingRooms = [];
              for (const selectedRoomId of this.selection.getSelectedRooms()) {
                const selectedRoom = this.dungeon.rooms.get(selectedRoomId);
                if (selectedRoom) {
                  this.draggingRooms.push(selectedRoom);
                }
              }
            } else {
              // Drag just this room (single selection or clicking unselected room)
              this.draggingRooms = [room];
            }
            this.dragStartHex = hex;
            this.dragCurrentHex = hex;
          }
          // If clicking edge of room with interior, allow corridor drawing
        }
      }
    });

    // Mouse moves while drawing
    this.pointer.on("move", ({ position, hex }) => {
      this.lastStrokeEndPosition = position;

      // Room dragging mode (single or multi)
      if (this.draggingRooms.length > 0 && hex) {
        this.dragCurrentHex = hex;
        this.renderRoomDragPreview();
        return;
      }

      // Quick carve mode - show preview (shift/alt + right-click)
      if (this.quickCarveStart && this.quickCarveType) {
        this.renderQuickCarvePreview(position);
        return;
      }

      // Subdivision stroke preview (right-click drawing on selected room)
      const subdivisionStroke = this.pointer.getSubdivisionStroke();
      if (subdivisionStroke.length > 0) {
        console.log("Subdivision stroke preview:", subdivisionStroke.length, "points");
        this.renderSubdivisionPreview(subdivisionStroke);
        return;
      }

      // Quick shape mode - show preview
      if (this.quickShapeStart && this.quickShapeType) {
        this.renderQuickShapePreview(position);
      } else {
        this.renderDrawingPreview();
        this.updateStrokeFeedback(position);
      }
    });

    // Clear highlight on pointer up
    this.pointer.on("up", ({ hex }) => {
      // Complete room drag if active (single or multi)
      if (this.draggingRooms.length > 0 && this.dragStartHex && this.dragCurrentHex) {
        const deltaQ = this.dragCurrentHex.q - this.dragStartHex.q;
        const deltaR = this.dragCurrentHex.r - this.dragStartHex.r;

        // Only commit move if actually moved
        if (deltaQ !== 0 || deltaR !== 0) {
          this.commitRoomsMove(this.draggingRooms, deltaQ, deltaR);
        }

        this.draggingRooms = [];
        this.dragStartHex = null;
        this.dragCurrentHex = null;
        this.wasRoomDrag = true; // Flag to skip stroke processing
      }

      // Complete quick carve mode if active (only if actually dragged)
      if (this.quickCarveStart && this.quickCarveType && hex) {
        // Only carve shape if we actually moved from the start position
        const movedFromStart = hex.q !== this.quickCarveStart.q || hex.r !== this.quickCarveStart.r;
        if (movedFromStart) {
          this.completeQuickCarve(hex);
        }
        this.quickCarveStart = null;
        this.quickCarveType = null;
      }

      // Complete quick shape mode if active (only if actually dragged)
      if (this.quickShapeStart && this.quickShapeType && hex) {
        // Only create shape if we actually moved from the start position
        const movedFromStart = hex.q !== this.quickShapeStart.q || hex.r !== this.quickShapeStart.r;
        if (movedFromStart) {
          this.completeQuickShape(hex);
        }
        this.quickShapeStart = null;
        this.quickShapeType = null;
      }

      this.previewHexes = null;
      this.clearPreviewLayer();
      this.strokeFeedback.hide();
    });

    // Tap to select room or corridor (left-click only)
    // Ctrl+click adds to selection (multi-select)
    this.pointer.on("tap", ({ hex, button, ctrlKey }) => {
      // Only handle left-click taps for selection
      if (button !== 0) return;

      if (hex) {
        const key = hexToKey(hex);
        selectAtHex(key, this.dungeon, this.selection, ctrlKey);

        const selectedRooms = this.selection.getSelectedRooms();
        const selectedCorridor = this.selection.getSelectedCorridor();

        // Enable subdivision mode when exactly one room is selected
        this.pointer.setSubdivisionMode(selectedRooms.size === 1);

        if (selectedRooms.size > 1) {
          console.log(`Selected ${selectedRooms.size} rooms (drag to move together)`);
        } else if (selectedRooms.size === 1) {
          const roomId = selectedRooms.values().next().value!;
          const room = this.dungeon.rooms.get(roomId);
          console.log(`Selected room: ${room?.id}, label: "${room?.label}" (right-click to subdivide, ctrl+click to multi-select)`);
        } else if (selectedCorridor) {
          console.log(`Selected corridor: ${selectedCorridor}`);
        } else {
          console.log(`Tapped hex: (${hex.q}, ${hex.r})`);
        }

        // Re-render to show selection highlight
        this.renderRooms();
        this.renderSelectionHighlight();
      }
    });

    // Double-tap to annotate room (left-click only)
    this.pointer.on("doubletap", ({ hex, position, button }) => {
      // Only handle left-click double-taps
      if (button !== 0) return;

      if (hex) {
        const key = hexToKey(hex);
        const room = this.dungeon.getRoom(key);
        if (room) {
          // Calculate room centroid for annotation position
          const roomCenter = this.getRoomCenter(room);
          this.annotations.show(room, roomCenter);
        }
      }
    });

    // Stroke completion - create room if closed shape
    this.pointer.onStroke((stroke) => {
      this.handleStroke(stroke);
    });

    // Subdivision stroke completion (right-click drawing when room selected)
    this.pointer.onSubdivisionStroke((stroke) => {
      this.handleSubdivisionStroke(stroke);
    });

    // Pan viewport (right-click or middle-click drag)
    // Use CSS transform for instant feedback during drag
    this.pointer.onPan((delta) => {
      this.layers.setTransform(delta.x, delta.y);
    });

    // When pan ends, commit transform to layout and re-render
    this.pointer.onPanEnd((totalDelta) => {
      this.handlePanEnd(totalDelta);
    });

    // Zoom viewport (scroll wheel)
    this.pointer.onZoom((zoomDelta, center, modifiers) => {
      if (modifiers.ctrlKey && this.selection.getSelectedRooms().size > 0) {
        this.handleRoomScale(zoomDelta);
      } else {
        this.handleZoom(zoomDelta, center);
      }
    });

    // Handle window resize
    window.addEventListener("resize", () => {
      this.handleResize();
    });
  }

  /**
   * Set up keyboard shortcuts for undo/redo
   */
  private setupKeyboardShortcuts(): void {
    // Undo (Ctrl+Z)
    this.keyboard.register({
      key: "z",
      ctrl: true,
      description: "Undo",
      handler: () => {
        if (this.undoManager.undo()) {
          console.log("Undo");
        }
      },
    });

    // Redo (Ctrl+Y)
    this.keyboard.register({
      key: "y",
      ctrl: true,
      description: "Redo",
      handler: () => {
        if (this.undoManager.redo()) {
          console.log("Redo");
        }
      },
    });

    // Toggle undo tree panel (Ctrl+U)
    this.keyboard.register({
      key: "u",
      ctrl: true,
      description: "Toggle undo tree panel",
      handler: () => {
        this.undoTreePanel.toggle();
      },
    });

    // Prune history up to current node (Ctrl+K)
    this.keyboard.register({
      key: "k",
      ctrl: true,
      description: "Clear history up to current node",
      handler: () => {
        const removed = this.undoManager.pruneHistory();
        if (removed > 0) {
          console.log(`Pruned ${removed} history node(s)`);
        }
      },
    });

    // Arrow key navigation when panel is open
    this.keyboard.register({
      key: "ArrowUp",
      description: "Navigate up in undo tree",
      handler: () => {
        if (this.undoTreePanel.isVisible()) {
          this.undoTreePanel.handleKeyboardNavigation("ArrowUp");
        }
      },
    });

    this.keyboard.register({
      key: "ArrowDown",
      description: "Navigate down in undo tree",
      handler: () => {
        if (this.undoTreePanel.isVisible()) {
          this.undoTreePanel.handleKeyboardNavigation("ArrowDown");
        }
      },
    });

    this.keyboard.register({
      key: "ArrowLeft",
      description: "Navigate left in undo tree",
      handler: () => {
        if (this.undoTreePanel.isVisible()) {
          this.undoTreePanel.handleKeyboardNavigation("ArrowLeft");
        }
      },
    });

    this.keyboard.register({
      key: "ArrowRight",
      description: "Navigate right in undo tree",
      handler: () => {
        if (this.undoTreePanel.isVisible()) {
          this.undoTreePanel.handleKeyboardNavigation("ArrowRight");
        }
      },
    });

    // Delete selected items (Delete or Backspace)
    this.keyboard.register({
      key: "Delete",
      description: "Delete selected room or corridor",
      handler: () => {
        this.deleteSelection();
      },
    });

    this.keyboard.register({
      key: "Backspace",
      description: "Delete selected room or corridor",
      handler: () => {
        this.deleteSelection();
      },
    });

    // Export to PNG (Ctrl+E)
    this.keyboard.register({
      key: "e",
      ctrl: true,
      description: "Export map as PNG",
      handler: () => {
        this.exportToPng();
      },
    });
  }

  /**
   * Delete the currently selected room(s) or corridor
   */
  private deleteSelection(): void {
    const selectedRooms = this.selection.getSelectedRooms();
    const selectedCorridorId = this.selection.getSelectedCorridor();

    if (selectedRooms.size === 0 && !selectedCorridorId) {
      return; // Nothing selected
    }

    const deletedRooms: ReturnType<typeof serializeRoom>[] = [];
    const deletedCorridors: ReturnType<typeof serializeCorridor>[] = [];

    // Delete selected rooms
    if (selectedRooms.size > 0) {
      for (const roomId of selectedRooms) {
        const room = this.dungeon.rooms.get(roomId);
        if (room) {
          deletedRooms.push(serializeRoom(room));

          // Also capture corridors connected to this room
          const connections = this.dungeon.getRoomConnections(roomId);
          for (const corridor of connections) {
            deletedCorridors.push(serializeCorridor(corridor));
          }

          this.dungeon.removeRoom(roomId);
        }
      }
    }

    // Delete selected corridor
    if (selectedCorridorId) {
      const corridor = this.dungeon.corridors.get(selectedCorridorId);
      if (corridor) {
        deletedCorridors.push(serializeCorridor(corridor));
        this.dungeon.removeCorridor(selectedCorridorId);
      }
    }

    // Record undo if anything was deleted
    const totalDeleted = deletedRooms.length + deletedCorridors.length;
    if (totalDeleted > 0) {
      const delta: OperationDelta = {
        type: "deleteScribble",
        rooms: deletedRooms,
        corridors: deletedCorridors,
      };

      let label = "Delete";
      if (deletedRooms.length > 0 && deletedCorridors.length > 0) {
        label = `Delete ${deletedRooms.length} room${deletedRooms.length > 1 ? "s" : ""} and corridors`;
      } else if (deletedRooms.length > 0) {
        label = deletedRooms.length === 1 ? "Delete room" : `Delete ${deletedRooms.length} rooms`;
      } else {
        label = "Delete corridor";
      }

      this.undoManager.recordOperation("deleteScribble", label, delta);

      // Clear selection and re-render
      this.selection.clear();
      this.renderRooms();
      this.updateDesires();
    }
  }

  /**
   * Handle pan end - commit the CSS transform to layout and re-render
   */
  private handlePanEnd(totalDelta: { x: number; y: number }): void {
    // Update origin with the total pan distance
    this.layout.origin.x += totalDelta.x;
    this.layout.origin.y += totalDelta.y;

    // Reset CSS transform
    this.layers.resetTransform();

    // Update pointer handler with new layout
    this.pointer.setLayout(this.layout);

    // Re-render everything at new position
    this.renderGrid();
    this.renderRooms();
    this.renderSelectionHighlight();
  }

  /**
   * Handle zoom gesture - scale the viewport centered on cursor position
   */
  private handleZoom(zoomDelta: number, center: Point): void {
    const zoomFactor = 1.15; // 15% zoom per scroll step
    const oldSize = this.layout.size;

    // Calculate new size based on zoom direction
    const newSize = zoomDelta > 0
      ? oldSize * zoomFactor
      : oldSize / zoomFactor;

    // Clamp size to reasonable bounds (min 10, max 80 pixels per hex)
    this.layout.size = Math.max(10, Math.min(80, newSize));

    // If size didn't change (hit bounds), skip re-render
    if (this.layout.size === oldSize) {
      return;
    }

    // Adjust origin to zoom toward cursor position
    // The cursor position should map to the same world coordinate before and after zoom
    const scale = this.layout.size / oldSize;

    // Calculate offset from origin to cursor
    const offsetX = center.x - this.layout.origin.x;
    const offsetY = center.y - this.layout.origin.y;

    // Scale the offset and compute new origin
    this.layout.origin.x = center.x - offsetX * scale;
    this.layout.origin.y = center.y - offsetY * scale;

    // Update pointer handler with new layout
    this.pointer.setLayout(this.layout);

    // Re-render everything
    this.renderGrid();
    this.renderRooms();
    this.renderSelectionHighlight();
  }

  /**
   * Handle room scaling with Ctrl+scroll (preview mode)
   */
  private handleRoomScale(zoomDelta: number): void {
    const selectedRoomIds = this.selection.getSelectedRooms();
    if (selectedRoomIds.size === 0) return;

    const selectedRooms = [...selectedRoomIds]
      .map(id => this.dungeon.rooms.get(id))
      .filter((r): r is Room => r !== undefined);

    if (selectedRooms.length === 0) return;

    // On first scroll, capture original state for later undo
    if (!this.scalingPreviewActive) {
      this.scalingPreviewActive = true;
      this.scalingOriginalRooms.clear();

      // Capture all rooms (not just selected - pushed rooms may change too)
      for (const [id, room] of this.dungeon.rooms) {
        this.scalingOriginalRooms.set(id, serializeRoom(room));
      }
      this.scalingOriginalCorridors = [...this.dungeon.corridors.values()].map(serializeCorridor);

      // Calculate and store center (use original positions)
      this.scalingCenter = this.calculateRoomsCenter(selectedRooms);
    }

    if (!this.scalingCenter) return;

    if (zoomDelta > 0) {
      this.expandRoomsPreview(selectedRooms, this.scalingCenter);
    } else {
      this.contractRoomsPreview(selectedRooms, this.scalingCenter);
    }
  }

  /**
   * Commit the scaling preview and create a single undo entry
   */
  private commitScalingPreview(): void {
    if (!this.scalingPreviewActive) return;

    // Compute what changed
    const roomChanges: Array<{
      roomId: string;
      addedHexes: string[];
      removedHexes: string[];
    }> = [];

    const pushedRooms: Array<{
      roomId: string;
      deltaQ: number;
      deltaR: number;
      roomBefore: ReturnType<typeof serializeRoom>;
    }> = [];

    // Compare current state to original for each room
    for (const [roomId, originalRoom] of this.scalingOriginalRooms) {
      const currentRoom = this.dungeon.rooms.get(roomId);

      if (!currentRoom) {
        // Room was deleted (shouldn't happen in scaling, but handle it)
        continue;
      }

      const originalHexes = new Set(originalRoom.hexes);
      const currentHexes = currentRoom.hexes;

      // Find added and removed hexes
      const addedHexes: string[] = [];
      const removedHexes: string[] = [];

      for (const hex of currentHexes) {
        if (!originalHexes.has(hex)) {
          addedHexes.push(hex);
        }
      }

      for (const hex of originalHexes) {
        if (!currentHexes.has(hex)) {
          removedHexes.push(hex);
        }
      }

      // Check if room was moved (pushed)
      if (addedHexes.length === 0 && removedHexes.length === 0) {
        // No hex changes - but room might have been pushed
        // Check if all hexes shifted by a constant delta
        const origFirst = originalRoom.hexes[0];
        const currFirstKey = [...currentHexes][0];
        if (origFirst && currFirstKey) {
          const [oq, or] = origFirst.split(",").map(Number);
          const [cq, cr] = currFirstKey.split(",").map(Number);
          const deltaQ = cq - oq;
          const deltaR = cr - or;

          if (deltaQ !== 0 || deltaR !== 0) {
            // Room was pushed - verify all hexes shifted by same delta
            let allShifted = true;
            for (const origHex of originalRoom.hexes) {
              const [q, r] = origHex.split(",").map(Number);
              const expectedKey = `${q + deltaQ},${r + deltaR}`;
              if (!currentHexes.has(expectedKey)) {
                allShifted = false;
                break;
              }
            }

            if (allShifted) {
              pushedRooms.push({
                roomId,
                deltaQ,
                deltaR,
                roomBefore: originalRoom,
              });
            }
          }
        }
      } else if (addedHexes.length > 0 || removedHexes.length > 0) {
        roomChanges.push({ roomId, addedHexes, removedHexes });
      }
    }

    const corridorsAfter = [...this.dungeon.corridors.values()].map(serializeCorridor);

    // Only record undo if something changed
    if (roomChanges.length > 0 || pushedRooms.length > 0) {
      const delta: OperationDelta = {
        type: "scaleRooms",
        roomChanges,
        pushedRooms,
        corridorsBefore: this.scalingOriginalCorridors,
        corridorsAfter,
      };

      const scaledCount = roomChanges.length;
      const pushedCount = pushedRooms.length;
      let label = "Scale rooms";
      if (scaledCount === 1 && pushedCount === 0) {
        label = "Scale room";
      } else if (scaledCount > 0 && pushedCount > 0) {
        label = `Scale ${scaledCount} room${scaledCount > 1 ? "s" : ""}, push ${pushedCount}`;
      }

      this.undoManager.recordOperation("scaleRooms", label, delta);
    }

    // Clear preview state
    this.scalingPreviewActive = false;
    this.scalingOriginalRooms.clear();
    this.scalingOriginalCorridors = [];
    this.scalingCenter = null;

    this.updateMinimap();
  }

  /**
   * Calculate the geometric center of multiple rooms
   */
  private calculateRoomsCenter(rooms: Room[]): Point | null {
    let totalX = 0;
    let totalY = 0;
    let totalHexes = 0;

    for (const room of rooms) {
      const center = this.dungeon.getRoomCenter(room.id, this.layout);
      if (center) {
        totalX += center.x * room.hexes.size;
        totalY += center.y * room.hexes.size;
        totalHexes += room.hexes.size;
      }
    }

    if (totalHexes === 0) return null;

    return {
      x: totalX / totalHexes,
      y: totalY / totalHexes,
    };
  }

  /**
   * Expand selected rooms radially from center (preview mode - no undo recording)
   */
  private expandRoomsPreview(rooms: Room[], center: Point): void {
    const selectedRoomIds = new Set(rooms.map(r => r.id));

    // Track pushed rooms for collision resolution
    const pushedRooms: Array<{ roomId: string; deltaQ: number; deltaR: number; roomBefore: ReturnType<typeof serializeRoom> }> = [];

    // Calculate expansion for each room
    for (const room of rooms) {
      const expansionHexes = calculateRadialExpansion(room.hexes, center, this.layout);

      if (expansionHexes.size === 0) continue;

      // Check each expansion hex for collisions
      const addedHexes: string[] = [];

      for (const hexKey of expansionHexes) {
        // Check if hex belongs to another room
        const existingRoomId = this.dungeon.getRoom(hexKey)?.id;

        if (existingRoomId && !selectedRoomIds.has(existingRoomId)) {
          // Collision with non-selected room - push it away
          const pushed = this.pushRoomAway(existingRoomId, center, selectedRoomIds, pushedRooms);
          if (!pushed) {
            // Can't push - skip this expansion hex
            continue;
          }
        }

        addedHexes.push(hexKey);
      }

      // Apply expansion
      if (addedHexes.length > 0) {
        this.dungeon.extendRoom(room.id, new Set(addedHexes));
      }
    }

    // Handle corridor absorption - reroute any corridors that now pass through rooms
    this.handleCorridorAbsorption();

    // Re-render (no undo recording - that happens on commit)
    this.renderRooms();
    this.renderSelectionHighlight();
  }

  /**
   * Contract selected rooms radially toward center (preview mode - no undo recording)
   */
  private contractRoomsPreview(rooms: Room[], center: Point): void {
    for (const room of rooms) {
      const contractionHexes = calculateRadialContraction(room.hexes, center, this.layout);

      if (contractionHexes.size === 0) continue;

      // Remove hexes from room
      for (const hexKey of contractionHexes) {
        this.dungeon.removeHexFromRoom(room.id, hexKey);
      }
    }

    // Re-render (no undo recording - that happens on commit)
    this.renderRooms();
    this.renderSelectionHighlight();
  }

  /**
   * Push a room away from the scaling center
   */
  private pushRoomAway(
    roomId: string,
    fromCenter: Point,
    excludeRoomIds: Set<string>,
    pushedRooms: Array<{ roomId: string; deltaQ: number; deltaR: number; roomBefore: ReturnType<typeof serializeRoom> }>,
    depth: number = 0
  ): boolean {
    if (depth > 10) return false; // Prevent infinite recursion

    const room = this.dungeon.rooms.get(roomId);
    if (!room) return false;

    // Already pushed this room
    if (pushedRooms.some(p => p.roomId === roomId)) return true;

    const roomBefore = serializeRoom(room);
    const roomCenter = this.dungeon.getRoomCenter(roomId, this.layout);
    if (!roomCenter) return false;

    // Direction from scaling center to room center
    const dx = roomCenter.x - fromCenter.x;
    const dy = roomCenter.y - fromCenter.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 0.1) {
      // Room is at center, push in arbitrary direction
      return false;
    }

    // Normalize direction
    const ndx = dx / dist;
    const ndy = dy / dist;

    // Convert pixel direction to hex delta (approximate)
    // Hex size determines scale
    const hexSize = this.layout.size;
    const pushDistance = hexSize * 1.5; // Push by roughly one hex

    // Try to find a valid hex delta that moves the room away
    const targetX = roomCenter.x + ndx * pushDistance;
    const targetY = roomCenter.y + ndy * pushDistance;

    // Convert target position to hex and find delta
    const currentHex = pixelToHex({ x: roomCenter.x, y: roomCenter.y }, this.layout);
    const targetHex = pixelToHex({ x: targetX, y: targetY }, this.layout);

    const deltaQ = targetHex.q - currentHex.q;
    const deltaR = targetHex.r - currentHex.r;

    if (deltaQ === 0 && deltaR === 0) {
      // Need to move at least one hex
      // Pick the direction based on the angle
      const angle = Math.atan2(ndy, ndx);
      // Map angle to one of 6 hex directions
      const dirIndex = Math.round((angle / (Math.PI / 3)) + 3) % 6;
      const directions = [
        { q: 1, r: 0 },   // E
        { q: 1, r: -1 },  // NE
        { q: 0, r: -1 },  // NW
        { q: -1, r: 0 },  // W
        { q: -1, r: 1 },  // SW
        { q: 0, r: 1 },   // SE
      ];
      const dir = directions[dirIndex];
      return this.tryPushRoom(roomId, dir.q, dir.r, fromCenter, excludeRoomIds, pushedRooms, roomBefore, depth);
    }

    return this.tryPushRoom(roomId, deltaQ, deltaR, fromCenter, excludeRoomIds, pushedRooms, roomBefore, depth);
  }

  /**
   * Try to push a room by a specific delta
   */
  private tryPushRoom(
    roomId: string,
    deltaQ: number,
    deltaR: number,
    fromCenter: Point,
    excludeRoomIds: Set<string>,
    pushedRooms: Array<{ roomId: string; deltaQ: number; deltaR: number; roomBefore: ReturnType<typeof serializeRoom> }>,
    roomBefore: ReturnType<typeof serializeRoom>,
    depth: number
  ): boolean {
    const room = this.dungeon.rooms.get(roomId);
    if (!room) return false;

    // Check what this room would collide with after moving
    const newHexes = new Set<string>();
    for (const hexKey of room.hexes) {
      const hex = keyToHex(hexKey);
      const newKey = hexToKey({ q: hex.q + deltaQ, r: hex.r + deltaR });
      newHexes.add(newKey);
    }

    // Check for collisions with other rooms
    for (const newHexKey of newHexes) {
      const existingRoomId = this.dungeon.getRoom(newHexKey)?.id;
      if (existingRoomId && existingRoomId !== roomId && !excludeRoomIds.has(existingRoomId)) {
        // Would collide with another room - push that one first
        const pushed = this.pushRoomAway(existingRoomId, fromCenter, excludeRoomIds, pushedRooms, depth + 1);
        if (!pushed) return false;
      }
    }

    // Now move this room
    const allExcluded = new Set([...excludeRoomIds, roomId]);
    const moved = this.dungeon.moveRoom(roomId, deltaQ, deltaR, allExcluded);
    if (!moved) return false;

    pushedRooms.push({ roomId, deltaQ, deltaR, roomBefore });
    return true;
  }

  /**
   * Handle corridors that now pass through expanded rooms
   */
  private handleCorridorAbsorption(): ReturnType<typeof serializeCorridor>[] {
    // Get all room hexes
    const roomHexes = new Set<string>();
    for (const room of this.dungeon.rooms.values()) {
      for (const hexKey of room.hexes) {
        roomHexes.add(hexKey);
      }
    }

    // Check each corridor
    const corridorsToReroute: Corridor[] = [];
    for (const corridor of this.dungeon.corridors.values()) {
      // Check if any corridor hex is now inside a room
      for (const hexKey of corridor.hexes) {
        if (roomHexes.has(hexKey)) {
          corridorsToReroute.push(corridor);
          break;
        }
      }
    }

    // Reroute affected corridors
    for (const corridor of corridorsToReroute) {
      const [roomAId, roomBId] = corridor.connects;
      const roomA = this.dungeon.rooms.get(roomAId);
      const roomB = this.dungeon.rooms.get(roomBId);

      if (roomA && roomB) {
        const newPath = recalculateCorridorPath(roomA, roomB, this.layout);
        if (newPath && newPath.length > 0) {
          this.dungeon.updateCorridorPath(corridor.id, newPath);
        } else {
          // Can't find a path - remove the corridor
          this.dungeon.corridors.delete(corridor.id);
        }
      }
    }

    return [...this.dungeon.corridors.values()].map(serializeCorridor);
  }

  private handleResize(): void {
    // Update origin to stay centered
    const { width, height } = this.getContainerSize();
    this.layout.origin = { x: width / 2, y: height / 2 };
    this.pointer.setLayout(this.layout);

    // Re-render everything (desires rendered by animation loop)
    this.renderGrid();
    this.renderRooms();
  }

  /**
   * Render a preview of quick shape (rectangle or circle) while dragging
   */
  private renderQuickShapePreview(position: Point): void {
    const previewLayer = this.layers.getLayer("preview");
    if (!previewLayer || !this.quickShapeStart) return;

    this.layers.clearLayer("preview");

    const endHex = pixelToHex(position, this.layout);
    let hexes: Set<string>;

    if (this.quickShapeType === "rectangle") {
      // Rectangle from start corner to current position
      hexes = this.generateRectangleFromCorners(this.quickShapeStart, endHex);
    } else {
      // Circle centered at start, radius to current position
      const radius = hexDistance(this.quickShapeStart, endHex);
      hexes = generateCircle(this.quickShapeStart, Math.max(1, Math.round(radius)));
    }

    // Check for overlaps
    const overlaps = this.dungeon.findOverlappingRooms(hexes);
    const hasOverlap = overlaps.size > 0;

    const color = hasOverlap
      ? "rgba(200, 80, 80, 0.3)"
      : "rgba(100, 200, 100, 0.4)";

    this.renderer.renderHexesPreview(previewLayer.ctx, hexes, this.layout, color);

    // Show size indicator
    const label = this.quickShapeType === "rectangle" ? "RECT" : "CIRCLE";
    previewLayer.ctx.save();
    previewLayer.ctx.font = "bold 12px system-ui, sans-serif";
    previewLayer.ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    previewLayer.ctx.textAlign = "center";
    previewLayer.ctx.fillText(
      `${label} (${hexes.size} hexes)`,
      position.x,
      position.y - 20
    );
    previewLayer.ctx.restore();
  }

  /**
   * Generate a rectangle from two corner hexes
   */
  private generateRectangleFromCorners(corner1: Hex, corner2: Hex): Set<string> {
    const hexes = new Set<string>();
    const minQ = Math.min(corner1.q, corner2.q);
    const maxQ = Math.max(corner1.q, corner2.q);
    const minR = Math.min(corner1.r, corner2.r);
    const maxR = Math.max(corner1.r, corner2.r);

    for (let q = minQ; q <= maxQ; q++) {
      for (let r = minR; r <= maxR; r++) {
        hexes.add(hexToKey({ q, r }));
      }
    }

    return hexes;
  }

  /**
   * Complete quick shape creation
   */
  private completeQuickShape(endHex: Hex): void {
    if (!this.quickShapeStart) return;

    let hexes: Set<string>;

    if (this.quickShapeType === "rectangle") {
      hexes = this.generateRectangleFromCorners(this.quickShapeStart, endHex);
    } else {
      const radius = hexDistance(this.quickShapeStart, endHex);
      hexes = generateCircle(this.quickShapeStart, Math.max(1, Math.round(radius)));
    }

    if (hexes.size < 1) return;

    // Check for overlaps - use extend/merge behavior
    const overlaps = this.dungeon.findOverlappingRooms(hexes);
    const overlappingRoomIds = Array.from(overlaps.keys());

    if (overlappingRoomIds.length > 1) {
      // Capture rooms before merge for undo
      const removedRooms = overlappingRoomIds.map(id => serializeRoom(this.dungeon.rooms.get(id)!));
      const removedCorridors: ReturnType<typeof serializeCorridor>[] = [];
      const updatedCorridors: { corridorId: string; oldConnects: [string, string]; newConnects: [string, string] }[] = [];

      // Track corridors that will be affected
      for (const roomId of overlappingRoomIds.slice(1)) {
        for (const corridor of this.dungeon.getRoomConnections(roomId)) {
          const otherRoomId = corridor.connects[0] === roomId ? corridor.connects[1] : corridor.connects[0];
          if (overlappingRoomIds.includes(otherRoomId)) {
            removedCorridors.push(serializeCorridor(corridor));
          } else {
            updatedCorridors.push({
              corridorId: corridor.id,
              oldConnects: [...corridor.connects] as [string, string],
              newConnects: [overlappingRoomIds[0], otherRoomId] as [string, string],
            });
          }
        }
      }

      // Merge rooms
      const mergedRoom = this.dungeon.mergeRooms(overlappingRoomIds);
      if (mergedRoom) {
        const nonOverlapping = new Set([...hexes].filter(h => !this.dungeon.getRoom(h)));
        if (nonOverlapping.size > 0) {
          this.dungeon.extendRoom(mergedRoom.id, nonOverlapping);
        }

        // Record undo
        const delta: OperationDelta = {
          type: "mergeRooms",
          removedRooms,
          newRoom: serializeRoom(mergedRoom),
          removedCorridors,
          updatedCorridors,
        };
        this.undoManager.recordOperation("mergeRooms", `Merge ${overlappingRoomIds.length} rooms`, delta);

        this.strokeFeedback.showResult(
          `${this.quickShapeType} merged ${overlappingRoomIds.length} rooms`,
          "success",
          this.lastStrokeEndPosition
        );
        this.updateDesires();
      }
    } else if (overlappingRoomIds.length === 1) {
      // Extend existing room
      const roomId = overlappingRoomIds[0];
      const overlapHexes = overlaps.get(roomId)!;
      const nonOverlapping = new Set([...hexes].filter(h => !overlapHexes.has(h)));

      if (nonOverlapping.size > 0) {
        this.dungeon.extendRoom(roomId, nonOverlapping);

        // Record undo
        const delta: OperationDelta = {
          type: "extendRoom",
          roomId,
          addedHexes: Array.from(nonOverlapping),
        };
        this.undoManager.recordOperation("extendRoom", `Extend room (+${nonOverlapping.size})`, delta);

        this.strokeFeedback.showResult(
          `${this.quickShapeType} extended (+${nonOverlapping.size})`,
          "success",
          this.lastStrokeEndPosition
        );
        this.updateDesires();
      }
    } else {
      // Create new room with snap animation
      // Capture values before animation since quickShapeType gets cleared
      const shapeLabel = this.quickShapeType === "rectangle" ? "RECT" : "CIRCLE";
      const hexCount = hexes.size;
      const feedbackPos = { ...this.lastStrokeEndPosition };

      this.animateSnapEffect(hexes, () => {
        const room = this.dungeon.addRoom(hexes, "", true);

        // Record undo
        const delta: OperationDelta = {
          type: "addRoom",
          room: serializeRoom(room),
        };
        this.undoManager.recordOperation("addRoom", `Quick ${shapeLabel} (${hexCount} hexes)`, delta);

        this.strokeFeedback.showResult(
          `${shapeLabel} (${hexCount} hexes)`,
          "success",
          feedbackPos
        );
        this.updateDesires();
        this.renderRooms();
      });
      return; // Exit early since renderRooms is called in animation callback
    }

    this.renderRooms();
  }

  /**
   * Render a preview of quick carve shape (rectangle or circle) while dragging
   */
  private renderQuickCarvePreview(position: Point): void {
    const previewLayer = this.layers.getLayer("preview");
    if (!previewLayer || !this.quickCarveStart) return;

    const selectedRoomId = this.selection.getSelectedRoom();
    if (!selectedRoomId) return;

    const room = this.dungeon.rooms.get(selectedRoomId);
    if (!room) return;

    this.layers.clearLayer("preview");
    const ctx = previewLayer.ctx;

    const endHex = pixelToHex(position, this.layout);
    let hexes: Set<string>;

    if (this.quickCarveType === "rectangle") {
      // Rectangle from start corner to current position
      hexes = this.generateRectangleFromCorners(this.quickCarveStart, endHex);
    } else {
      // Circle centered at start, radius to current position
      const radius = hexDistance(this.quickCarveStart, endHex);
      hexes = generateCircle(this.quickCarveStart, Math.max(1, Math.round(radius)));
    }

    // Find intersection with selected room (only these hexes will be carved)
    const intersectionHexes = new Set<string>();
    for (const hexKey of hexes) {
      if (room.hexes.has(hexKey)) {
        intersectionHexes.add(hexKey);
      }
    }

    // Show the carve preview
    if (intersectionHexes.size > 0) {
      // Show carve area in red/orange
      this.renderer.renderHexesPreview(ctx, intersectionHexes, this.layout, "rgba(255, 100, 50, 0.4)");

      // Show the full shape outline (including parts outside room)
      this.renderer.renderHexesPreview(ctx, hexes, this.layout, "rgba(255, 100, 50, 0.15)");
    } else {
      // Shape doesn't intersect room - show in grey
      this.renderer.renderHexesPreview(ctx, hexes, this.layout, "rgba(100, 100, 100, 0.3)");
    }

    // Show size indicator
    const label = this.quickCarveType === "rectangle" ? "CARVE RECT" : "CARVE CIRCLE";
    ctx.save();
    ctx.font = "bold 12px system-ui, sans-serif";
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.textAlign = "center";
    ctx.fillText(
      intersectionHexes.size > 0
        ? `${label} (${intersectionHexes.size} hexes)`
        : `${label} (outside room)`,
      position.x,
      position.y - 20
    );
    ctx.restore();

    // Also highlight the selected room outline
    this.renderer.renderSelectionOutline(ctx, room.hexes, this.layout, "rgba(255, 100, 100, 0.6)");
  }

  /**
   * Complete quick carve - carve shape from selected room
   */
  private completeQuickCarve(endHex: Hex): void {
    if (!this.quickCarveStart) return;

    const selectedRoomId = this.selection.getSelectedRoom();
    if (!selectedRoomId) {
      this.strokeFeedback.showResult("no room selected", "error", this.lastStrokeEndPosition);
      return;
    }

    const room = this.dungeon.rooms.get(selectedRoomId);
    if (!room) {
      this.strokeFeedback.showResult("room not found", "error", this.lastStrokeEndPosition);
      return;
    }

    let hexes: Set<string>;

    if (this.quickCarveType === "rectangle") {
      hexes = this.generateRectangleFromCorners(this.quickCarveStart, endHex);
    } else {
      const radius = hexDistance(this.quickCarveStart, endHex);
      hexes = generateCircle(this.quickCarveStart, Math.max(1, Math.round(radius)));
    }

    if (hexes.size < 1) return;

    // Capture original state before carve
    const originalHexes = Array.from(room.hexes);

    // Carve the shape from the room
    const newRoom = this.dungeon.carveRoom(selectedRoomId, hexes);

    if (newRoom) {
      // Record undo
      const delta: OperationDelta = {
        type: "carveRoom",
        originalRoomId: selectedRoomId,
        originalHexes,
        carvedRoom: serializeRoom(newRoom),
      };
      this.undoManager.recordOperation("carveRoom", `Carve ${newRoom.hexes.size} hexes`, delta);

      const shapeLabel = this.quickCarveType === "rectangle" ? "RECT" : "CIRCLE";
      this.strokeFeedback.showResult(
        `carved ${shapeLabel} (${newRoom.hexes.size} hexes)`,
        "success",
        this.lastStrokeEndPosition
      );
      console.log(`Carved ${shapeLabel} room ${newRoom.id} from ${selectedRoomId}`);

      // Select the new carved room
      this.selection.selectRoom(newRoom.id);
      this.pointer.setSubdivisionMode(true);

      this.renderRooms();
      this.updateDesires();
    } else {
      this.strokeFeedback.showResult("can't carve there", "error", this.lastStrokeEndPosition);
    }
  }

  /**
   * Update stroke feedback while drawing
   */
  private updateStrokeFeedback(position: Point): void {
    const strokePoints = this.pointer.getCurrentStroke();
    if (strokePoints.length < 3) {
      this.strokeFeedback.hide();
      return;
    }

    const feedback = analyzeStrokeInProgress(strokePoints);
    this.strokeFeedback.update(feedback, position);
  }

  /**
   * Handle completed stroke - create room if closed shape
   */
  private handleStroke(stroke: Stroke): void {
    // Skip stroke processing if this was a room drag
    if (this.wasRoomDrag) {
      this.wasRoomDrag = false;
      return;
    }

    // Use detailed classification for better detection
    const classification = classifyStrokeDetailed(stroke);
    console.log(`Stroke: ${classification.type} (${Math.round(classification.confidence * 100)}% conf, closeness: ${Math.round(classification.closeness * 100)}%)`, classification.metrics);

    if (classification.type === "closed") {
      // Apply auto-close if needed
      const processedStroke = classification.autoCloseApplied
        ? autoCloseStroke(stroke)
        : stroke;

      // Convert stroke to hex region
      const hexes = strokeToHexRegion(processedStroke, this.layout);

      if (hexes.size > 0) {
        // Check for overlaps with existing rooms
        const overlaps = this.dungeon.findOverlappingRooms(hexes);
        const overlappingRoomIds = Array.from(overlaps.keys());

        if (overlappingRoomIds.length > 1) {
          // Capture rooms before merge for undo
          const removedRooms = overlappingRoomIds.map(id => serializeRoom(this.dungeon.rooms.get(id)!));
          const removedCorridors: ReturnType<typeof serializeCorridor>[] = [];
          const updatedCorridors: { corridorId: string; oldConnects: [string, string]; newConnects: [string, string] }[] = [];

          // Track corridors that will be affected
          for (const roomId of overlappingRoomIds.slice(1)) {
            for (const corridor of this.dungeon.getRoomConnections(roomId)) {
              const otherRoomId = corridor.connects[0] === roomId ? corridor.connects[1] : corridor.connects[0];
              if (overlappingRoomIds.includes(otherRoomId)) {
                removedCorridors.push(serializeCorridor(corridor));
              } else {
                updatedCorridors.push({
                  corridorId: corridor.id,
                  oldConnects: [...corridor.connects] as [string, string],
                  newConnects: [overlappingRoomIds[0], otherRoomId] as [string, string],
                });
              }
            }
          }

          // Multiple rooms overlapped - MERGE them
          const mergedRoom = this.dungeon.mergeRooms(overlappingRoomIds);

          if (mergedRoom) {
            // Add the non-overlapping hexes from the new stroke
            const nonOverlapping = new Set([...hexes].filter(h => !this.dungeon.getRoom(h)));
            if (nonOverlapping.size > 0) {
              this.dungeon.extendRoom(mergedRoom.id, nonOverlapping);
            }

            // Record undo
            const delta: OperationDelta = {
              type: "mergeRooms",
              removedRooms,
              newRoom: serializeRoom(mergedRoom),
              removedCorridors,
              updatedCorridors,
            };
            this.undoManager.recordOperation("mergeRooms", `Merge ${overlappingRoomIds.length} rooms`, delta);

            this.strokeFeedback.showResult(
              `merged ${overlappingRoomIds.length} rooms`,
              "success",
              this.lastStrokeEndPosition
            );
            console.log(`Merged ${overlappingRoomIds.length} rooms into ${mergedRoom.id}`);
            this.renderRooms();
            this.updateDesires();
          }
        } else if (overlappingRoomIds.length === 1) {
          // Single room overlapped - check if EXTEND or just overlap
          const roomId = overlappingRoomIds[0];
          const overlapHexes = overlaps.get(roomId)!;

          if (this.dungeon.isExtensionPattern(roomId, hexes, overlapHexes)) {
            // EXTEND the room
            const nonOverlapping = new Set([...hexes].filter(h => !overlapHexes.has(h)));
            const room = this.dungeon.extendRoom(roomId, nonOverlapping);

            if (room) {
              // Record undo
              const delta: OperationDelta = {
                type: "extendRoom",
                roomId,
                addedHexes: Array.from(nonOverlapping),
              };
              this.undoManager.recordOperation("extendRoom", `Extend room (+${nonOverlapping.size})`, delta);

              this.strokeFeedback.showResult(
                `extended (+${nonOverlapping.size} hexes)`,
                "success",
                this.lastStrokeEndPosition
              );
              console.log(`Extended room ${roomId} with ${nonOverlapping.size} new hexes`);
              this.renderRooms();
              this.updateDesires();
            }
          } else {
            // Mostly overlapping - show hint about extending
            this.strokeFeedback.showResult(
              "draw outside to extend",
              "info",
              this.lastStrokeEndPosition
            );
            console.log("Stroke mostly overlaps existing room - draw further outside to extend");
          }
        } else {
          // No overlap - create new room with snap animation
          this.animateSnapEffect(hexes, () => {
            const room = this.dungeon.addRoom(hexes, "", true);
            console.log(`Created room: ${room.id} with ${hexes.size} hexes`);

            // Record undo
            const delta: OperationDelta = {
              type: "addRoom",
              room: serializeRoom(room),
            };
            this.undoManager.recordOperation("addRoom", `Add room (${hexes.size} hexes)`, delta);

            this.strokeFeedback.showResult(
              `room (${hexes.size} hexes)`,
              "success",
              this.lastStrokeEndPosition
            );

            this.renderRooms();
            this.updateDesires();
          });
        }
      } else {
        this.strokeFeedback.showResult("too small", "error", this.lastStrokeEndPosition);
      }
    } else if (classification.type === "scribble") {
      // Delete what's under the scribble (but not while dragging rooms)
      if (this.draggingRooms.length === 0) {
        this.handleScribbleDelete(stroke);
      }
    } else {
      // Line stroke - create corridor between rooms
      this.handleLineStroke(stroke);
    }
  }

  /**
   * Handle subdivision stroke (right-click drawing on selected room)
   * Line stroke = bisect room, closed shape = carve out interior room
   */
  private handleSubdivisionStroke(stroke: Stroke): void {
    const selectedRoomId = this.selection.getSelectedRoom();
    if (!selectedRoomId) {
      this.strokeFeedback.showResult("no room selected", "error", this.lastStrokeEndPosition);
      return;
    }

    const room = this.dungeon.rooms.get(selectedRoomId);
    if (!room) {
      this.strokeFeedback.showResult("room not found", "error", this.lastStrokeEndPosition);
      return;
    }

    // Classify the stroke
    const classification = classifyStrokeDetailed(stroke);

    if (classification.type === "closed" || stroke.isClosed) {
      // Closed shape - carve out interior room
      const processedStroke = classification.autoCloseApplied
        ? autoCloseStroke(stroke)
        : stroke;

      const carveHexes = strokeToHexRegion(processedStroke, this.layout);

      if (carveHexes.size === 0) {
        this.strokeFeedback.showResult("shape too small", "error", this.lastStrokeEndPosition);
        return;
      }

      // Capture original state before carve
      const originalHexes = Array.from(room.hexes);

      const newRoom = this.dungeon.carveRoom(selectedRoomId, carveHexes);

      if (newRoom) {
        // Record undo
        const delta: OperationDelta = {
          type: "carveRoom",
          originalRoomId: selectedRoomId,
          originalHexes,
          carvedRoom: serializeRoom(newRoom),
        };
        this.undoManager.recordOperation("carveRoom", `Carve ${newRoom.hexes.size} hexes`, delta);

        this.strokeFeedback.showResult(
          `carved ${newRoom.hexes.size} hexes`,
          "success",
          this.lastStrokeEndPosition
        );
        console.log(`Carved new room ${newRoom.id} from ${selectedRoomId}`);

        // Select the new carved room
        this.selection.selectRoom(newRoom.id);
        this.pointer.setSubdivisionMode(true);

        this.renderRooms();
        this.updateDesires();
      } else {
        this.strokeFeedback.showResult("can't carve there", "error", this.lastStrokeEndPosition);
      }
    } else {
      // Line stroke - bisect the room
      if (stroke.points.length < 2) {
        this.strokeFeedback.showResult("line too short", "error", this.lastStrokeEndPosition);
        return;
      }

      const lineStart = stroke.points[0];
      const lineEnd = stroke.points[stroke.points.length - 1];

      // Create hexToPixel function for the bisect method
      const hexToPixelFn = (q: number, r: number) => {
        return hexToPixel({ q, r }, this.layout);
      };

      // Capture state before bisect for undo
      const originalRoom = serializeRoom(room);
      const originalCorridors = this.dungeon.getRoomConnections(selectedRoomId).map(c => serializeCorridor(c));

      const result = this.dungeon.bisectRoom(selectedRoomId, lineStart, lineEnd, hexToPixelFn);

      if (result) {
        const [roomA, roomB] = result;

        // Capture new corridors for undo
        const addedCorridors = [
          ...this.dungeon.getRoomConnections(roomA.id),
          ...this.dungeon.getRoomConnections(roomB.id),
        ].map(c => serializeCorridor(c));

        // Record undo
        const delta: OperationDelta = {
          type: "bisectRoom",
          originalRoom,
          newRooms: [serializeRoom(roomA), serializeRoom(roomB)],
          removedCorridors: originalCorridors,
          addedCorridors,
        };
        this.undoManager.recordOperation("bisectRoom", `Split into ${roomA.hexes.size} + ${roomB.hexes.size}`, delta);

        this.strokeFeedback.showResult(
          `split into ${roomA.hexes.size} + ${roomB.hexes.size}`,
          "success",
          this.lastStrokeEndPosition
        );
        console.log(`Bisected room into ${roomA.id} and ${roomB.id}`);

        // Clear selection (original room is gone)
        this.selection.clear();
        this.pointer.setSubdivisionMode(false);

        this.renderRooms();
        this.updateDesires();
      } else {
        this.strokeFeedback.showResult("can't split there", "error", this.lastStrokeEndPosition);
      }
    }
  }

  /**
   * Handle scribble gesture - delete room or corridor under the scribble
   */
  private handleScribbleDelete(stroke: Stroke): void {
    // Find hexes the scribble passes through
    const scribbledHexes = new Set<string>();
    for (const point of stroke.points) {
      const hex = pixelToHex(point, this.layout);
      scribbledHexes.add(hexToKey(hex));
    }

    console.log(`Scribble detected - checking ${scribbledHexes.size} hexes for deletion`);

    // Find rooms and corridors that overlap with scribble
    const roomsToDelete = new Set<string>();
    const corridorsToDelete = new Set<string>();

    for (const hexKey of scribbledHexes) {
      const room = this.dungeon.getRoom(hexKey);
      if (room) {
        roomsToDelete.add(room.id);
        console.log(`Found room to delete: ${room.id} (userDrawn: ${room.userDrawn}, label: "${room.label}")`);
      }

      for (const corridor of this.dungeon.corridors.values()) {
        if (corridor.hexes.includes(hexKey)) {
          corridorsToDelete.add(corridor.id);
        }
      }
    }

    // Capture state before delete for undo
    const deletedRooms: ReturnType<typeof serializeRoom>[] = [];
    const deletedCorridors: ReturnType<typeof serializeCorridor>[] = [];

    // Capture rooms (including their connected corridors that will also be deleted)
    for (const roomId of roomsToDelete) {
      const room = this.dungeon.rooms.get(roomId);
      if (room) {
        deletedRooms.push(serializeRoom(room));
        // Also capture corridors connected to this room (they'll be deleted too)
        for (const corridor of this.dungeon.getRoomConnections(roomId)) {
          if (!corridorsToDelete.has(corridor.id)) {
            corridorsToDelete.add(corridor.id);
          }
        }
      }
    }

    // Capture corridors
    for (const corridorId of corridorsToDelete) {
      const corridor = this.dungeon.corridors.get(corridorId);
      if (corridor) {
        deletedCorridors.push(serializeCorridor(corridor));
      }
    }

    // Delete rooms (works for ALL rooms regardless of userDrawn flag)
    for (const roomId of roomsToDelete) {
      const room = this.dungeon.rooms.get(roomId);
      this.dungeon.removeRoom(roomId);
      console.log(`Deleted room: ${roomId} (userDrawn: ${room?.userDrawn})`);
    }

    // Delete corridors
    for (const corridorId of corridorsToDelete) {
      this.dungeon.removeCorridor(corridorId);
      console.log(`Deleted corridor: ${corridorId}`);
    }

    // Clear selection if deleted item was selected
    if (
      (this.selection.getSelectedRoom() &&
        roomsToDelete.has(this.selection.getSelectedRoom()!)) ||
      (this.selection.getSelectedCorridor() &&
        corridorsToDelete.has(this.selection.getSelectedCorridor()!))
    ) {
      this.selection.clear();
    }

    // Show feedback and record undo
    const totalDeleted = roomsToDelete.size + corridorsToDelete.size;
    if (totalDeleted > 0) {
      // Record undo
      const delta: OperationDelta = {
        type: "deleteScribble",
        rooms: deletedRooms,
        corridors: deletedCorridors,
      };
      this.undoManager.recordOperation("deleteScribble", `Delete ${totalDeleted} items`, delta);

      const parts: string[] = [];
      if (roomsToDelete.size > 0) parts.push(`${roomsToDelete.size} room${roomsToDelete.size > 1 ? 's' : ''}`);
      if (corridorsToDelete.size > 0) parts.push(`${corridorsToDelete.size} corridor${corridorsToDelete.size > 1 ? 's' : ''}`);
      this.strokeFeedback.showResult(`deleted ${parts.join(', ')}`, "info", this.lastStrokeEndPosition);
      this.renderRooms();
      this.updateDesires();
    } else {
      this.strokeFeedback.showResult("nothing to delete", "info", this.lastStrokeEndPosition);
    }
  }

  /**
   * Handle line stroke - create corridor between two rooms
   */
  private handleLineStroke(stroke: Stroke): void {
    if (stroke.points.length < 2) {
      return;
    }

    // Get start and end hexes
    const startPoint = stroke.points[0];
    const endPoint = stroke.points[stroke.points.length - 1];

    const startHex = pixelToHex(startPoint, this.layout);
    const endHex = pixelToHex(endPoint, this.layout);

    const startKey = hexToKey(startHex);
    const endKey = hexToKey(endHex);

    // Find rooms at start and end
    const startRoom = this.dungeon.getRoom(startKey);
    const endRoom = this.dungeon.getRoom(endKey);

    if (!startRoom || !endRoom) {
      // Provide specific feedback about what's missing
      if (!startRoom && !endRoom) {
        this.strokeFeedback.showResult("start & end outside rooms", "error", this.lastStrokeEndPosition);
      } else if (!startRoom) {
        this.strokeFeedback.showResult("start outside room", "error", this.lastStrokeEndPosition);
      } else {
        this.strokeFeedback.showResult("end outside room", "error", this.lastStrokeEndPosition);
      }
      console.log("Line stroke must start and end in different rooms to create a corridor");
      return;
    }

    if (startRoom.id === endRoom.id) {
      this.strokeFeedback.showResult("same room", "error", this.lastStrokeEndPosition);
      console.log("Cannot create corridor within the same room");
      return;
    }

    // Convert stroke points to simple Point array
    const strokePoints: Point[] = stroke.points.map((p) => ({ x: p.x, y: p.y }));

    // Find corridor path using A*
    const corridorPath = findCorridorPath(
      startRoom,
      endRoom,
      this.dungeon,
      this.layout,
      strokePoints
    );

    if (corridorPath.length === 0) {
      this.strokeFeedback.showResult("no valid path", "error", this.lastStrokeEndPosition);
      console.log("Could not find valid corridor path");
      return;
    }

    // Filter out hexes that are inside rooms (keep only the connecting path)
    const corridorHexes = corridorPath.filter(
      (key) => !startRoom.hexes.has(key) && !endRoom.hexes.has(key)
    );

    // Create corridor
    const corridor = this.dungeon.addCorridor(
      corridorHexes,
      startRoom.id,
      endRoom.id,
      true
    );

    // Record undo
    const delta: OperationDelta = {
      type: "addCorridor",
      corridor: serializeCorridor(corridor),
    };
    this.undoManager.recordOperation("addCorridor", `Add corridor (${corridorHexes.length} hexes)`, delta);

    // Show success feedback
    if (corridorHexes.length === 0) {
      this.strokeFeedback.showResult("connected (adjacent)", "success", this.lastStrokeEndPosition);
    } else {
      this.strokeFeedback.showResult(`corridor (${corridorHexes.length} hexes)`, "success", this.lastStrokeEndPosition);
    }

    console.log(`Created corridor: ${corridor.id} connecting ${startRoom.id} to ${endRoom.id}`);

    // Re-render
    this.renderRooms();
    this.updateDesires();
  }

  private renderGrid(): void {
    // Grid disabled - just clear the layer
    this.layers.clearLayer("grid");
  }

  /**
   * Render all rooms and corridors on the static layer
   */
  private renderRooms(): void {
    const staticLayer = this.layers.getLayer("static");
    if (!staticLayer) return;

    // Clear and redraw all rooms and corridors
    this.layers.clearLayer("static");

    // Render corridors first (so rooms draw on top)
    for (const corridor of this.dungeon.corridors.values()) {
      this.renderer.renderCorridor(corridor, this.layout);
    }

    // Compute color assignments for adjacent rooms
    const colorAssignments = this.dungeon.assignRoomColors();

    // Render rooms with adjacency-based coloring
    const allRooms = this.dungeon.getAllRooms();
    for (const room of allRooms) {
      const fillColor = this.dungeon.getRoomFillColor(room.id, colorAssignments);
      this.renderer.renderRoom(room, this.layout, fillColor);
    }

    // Render all room labels with smart placement
    this.renderer.renderAllRoomLabels(allRooms, this.layout);

    // Update minimap
    this.updateMinimap();
  }

  /**
   * Export the dungeon as a PNG image
   */
  private exportToPng(): void {
    // Collect all hex keys from rooms and corridors
    const allHexKeys = new Set<string>();
    for (const room of this.dungeon.rooms.values()) {
      for (const key of room.hexes) {
        allHexKeys.add(key);
      }
    }
    for (const corridor of this.dungeon.corridors.values()) {
      for (const key of corridor.hexes) {
        allHexKeys.add(key);
      }
    }

    if (allHexKeys.size === 0) {
      console.log("Nothing to export");
      return;
    }

    // Calculate bounding box in hex coordinates
    let minQ = Infinity, maxQ = -Infinity;
    let minR = Infinity, maxR = -Infinity;

    for (const key of allHexKeys) {
      const hex = keyToHex(key);
      minQ = Math.min(minQ, hex.q);
      maxQ = Math.max(maxQ, hex.q);
      minR = Math.min(minR, hex.r);
      maxR = Math.max(maxR, hex.r);
    }

    // Calculate pixel bounds with padding
    const padding = this.layout.size * 2;
    const hexWidth = Math.sqrt(3) * this.layout.size;
    const hexHeight = 2 * this.layout.size;

    // Create a temporary layout for export (origin at 0,0)
    const exportLayout: HexLayout = {
      size: this.layout.size,
      origin: { x: 0, y: 0 },
      spacing: this.layout.spacing,
    };

    // Calculate bounds using the temporary layout
    const topLeft = hexToPixel({ q: minQ, r: minR }, exportLayout);
    const bottomRight = hexToPixel({ q: maxQ, r: maxR }, exportLayout);

    // Adjust for hex size and add padding
    const minX = topLeft.x - hexWidth / 2 - padding;
    const minY = topLeft.y - this.layout.size - padding;
    const maxX = bottomRight.x + hexWidth / 2 + padding;
    const maxY = bottomRight.y + this.layout.size + padding;

    const canvasWidth = Math.ceil(maxX - minX);
    const canvasHeight = Math.ceil(maxY - minY);

    // Create offscreen canvas
    const offscreenCanvas = document.createElement("canvas");
    offscreenCanvas.width = canvasWidth;
    offscreenCanvas.height = canvasHeight;
    const ctx = offscreenCanvas.getContext("2d");
    if (!ctx) {
      console.error("Failed to create offscreen canvas context");
      return;
    }

    // Fill with transparent background (or white if you prefer)
    ctx.fillStyle = "rgba(255, 255, 255, 0)";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Create a renderer for the offscreen canvas
    const exportRenderer = new HexRenderer(offscreenCanvas);

    // Adjust layout origin to account for the offset
    const renderLayout: HexLayout = {
      size: this.layout.size,
      origin: { x: -minX, y: -minY },
      spacing: this.layout.spacing,
    };

    // Render grid lines for the hex area
    const gridBounds = {
      minQ: minQ - 1,
      maxQ: maxQ + 1,
      minR: minR - 1,
      maxR: maxR + 1,
    };
    exportRenderer.renderGrid(ctx, renderLayout, gridBounds, {
      stroke: "#cccccc",
      strokeWidth: 0.5,
    });

    // Render corridors first
    for (const corridor of this.dungeon.corridors.values()) {
      exportRenderer.renderCorridor(corridor, renderLayout);
    }

    // Render rooms with color assignments
    const colorAssignments = this.dungeon.assignRoomColors();
    const allRooms = this.dungeon.getAllRooms();
    for (const room of allRooms) {
      const fillColor = this.dungeon.getRoomFillColor(room.id, colorAssignments);
      exportRenderer.renderRoom(room, renderLayout, fillColor);
    }

    // Render labels
    exportRenderer.renderAllRoomLabels(allRooms, renderLayout);

    // Trigger download
    const link = document.createElement("a");
    link.download = `hexcrawl-${Date.now()}.png`;
    link.href = offscreenCanvas.toDataURL("image/png");
    link.click();

    console.log(`Exported ${canvasWidth}x${canvasHeight} PNG`);
  }

  /**
   * Update the minimap with current dungeon state
   */
  private updateMinimap(): void {
    const { width, height } = this.getContainerSize();

    // Calculate viewport bounds in world coordinates
    const viewportBounds = {
      x: -this.layout.origin.x,
      y: -this.layout.origin.y,
      width: width,
      height: height,
    };

    this.minimap.render(
      this.dungeon.rooms,
      this.dungeon.corridors,
      this.layout,
      viewportBounds
    );
  }

  /**
   * Navigate to a specific world position (center viewport on it)
   */
  private navigateToWorldPosition(worldX: number, worldY: number): void {
    const { width, height } = this.getContainerSize();

    // Set origin so the world position is at the center of the viewport
    this.layout.origin.x = width / 2 - worldX;
    this.layout.origin.y = height / 2 - worldY;

    // Update pointer handler with new layout
    this.pointer.setLayout(this.layout);

    // Re-render everything
    this.renderGrid();
    this.renderRooms();
    this.renderSelectionHighlight();
  }

  /**
   * Render selection highlight on preview layer
   */
  private renderSelectionHighlight(): void {
    // Selection highlight is now rendered as part of renderDesireLayer()
    // to avoid clearing the desire glows
  }

  private renderHoverHighlight(): void {
    // Hover highlight is now rendered as part of renderDesireLayer()
    // to avoid clearing the desire glows
  }

  private renderDrawingPreview(): void {
    const previewLayer = this.layers.getLayer("preview");
    if (!previewLayer) return;

    const strokePoints = this.pointer.getCurrentStroke();
    if (strokePoints.length < 2) return;

    // Clear and redraw
    this.layers.clearLayer("preview");

    // Show existing room boundaries subtly while drawing (constraint visualization)
    if (this.dungeon.rooms.size > 0) {
      this.renderer.renderConstraintBoundaries(
        previewLayer.ctx,
        this.dungeon.getAllRooms(),
        this.layout,
        "rgba(255, 255, 255, 0.08)"
      );
    }

    // Analyze the stroke in progress
    const stroke: Stroke = { points: strokePoints, isClosed: false };
    const closeness = getCloseness(stroke);

    // Draw the stroke preview with color based on detected type
    const points: Point[] = strokePoints.map((p) => ({ x: p.x, y: p.y }));
    let strokeColor = "#888"; // Default grey

    if (strokePoints.length >= 5) {
      if (closeness > 0.5) {
        // Looks like it could be a room - green
        strokeColor = "rgba(100, 200, 100, 0.8)";
      } else {
        // Likely a corridor - amber
        strokeColor = "rgba(200, 170, 100, 0.8)";
      }
    }

    this.renderer.renderStrokePreview(previewLayer.ctx, points, strokeColor);

    // Show hex region preview based on what we think the stroke will become
    if (strokePoints.length >= 8) {
      if (closeness > 0.3) {
        // Could be a room - show hex region preview
        // Use auto-close for preview if closeness is moderate
        const previewStroke = closeness > 0.5 && closeness < 0.85
          ? autoCloseStroke(stroke, 0.3)
          : stroke;

        const hexes = strokeToHexRegion(previewStroke, this.layout);
        if (hexes.size > 0) {
          this.previewHexes = hexes;

          // Check for overlaps and determine preview color
          const overlaps = this.dungeon.findOverlappingRooms(hexes);
          const overlappingRoomIds = Array.from(overlaps.keys());

          let previewColor: string;
          if (overlappingRoomIds.length > 1) {
            // Will merge multiple rooms - blue tint
            previewColor = "rgba(100, 150, 200, 0.3)";
          } else if (overlappingRoomIds.length === 1) {
            // Check if extend or overlap
            const roomId = overlappingRoomIds[0];
            const overlapHexes = overlaps.get(roomId)!;
            if (this.dungeon.isExtensionPattern(roomId, hexes, overlapHexes)) {
              // Will extend - cyan tint
              previewColor = "rgba(100, 200, 180, 0.3)";
            } else {
              // Mostly overlap - amber tint (needs to draw further out)
              previewColor = "rgba(200, 170, 100, 0.3)";
            }
          } else {
            // No overlap - green for new room
            previewColor = "rgba(100, 200, 100, 0.25)";
          }

          this.renderer.renderHexesPreview(
            previewLayer.ctx,
            hexes,
            this.layout,
            previewColor
          );
        }
      } else {
        // Likely a corridor - show potential corridor path preview
        this.renderCorridorPreviewWhileDrawing(previewLayer.ctx, strokePoints);
      }
    }

    // Also show hover highlight
    if (this.hoveredHex) {
      this.renderer.renderHexHighlight(
        previewLayer.ctx,
        this.hoveredHex,
        this.layout,
        "rgba(255, 200, 100, 0.3)"
      );
    }
  }

  /**
   * Render a preview of where a corridor would go while drawing
   */
  private renderCorridorPreviewWhileDrawing(
    ctx: CanvasRenderingContext2D,
    strokePoints: import("./types.ts").StrokePoint[]
  ): void {
    if (strokePoints.length < 5) return;

    const startPoint = strokePoints[0];
    const endPoint = strokePoints[strokePoints.length - 1];

    const startHex = pixelToHex(startPoint, this.layout);
    const endHex = pixelToHex(endPoint, this.layout);

    const startKey = hexToKey(startHex);
    const endKey = hexToKey(endHex);

    const startRoom = this.dungeon.getRoom(startKey);
    const endRoom = this.dungeon.getRoom(endKey);

    // Highlight start and end positions
    if (startRoom) {
      // Start is in a room - good
      this.renderer.renderHexHighlight(ctx, startHex, this.layout, "rgba(100, 200, 100, 0.4)");
    } else {
      // Start is not in a room - show warning
      this.renderer.renderHexHighlight(ctx, startHex, this.layout, "rgba(200, 100, 100, 0.4)");
    }

    if (endRoom && endRoom.id !== startRoom?.id) {
      // End is in a different room - good
      this.renderer.renderHexHighlight(ctx, endHex, this.layout, "rgba(100, 200, 100, 0.4)");
    } else if (endRoom && endRoom.id === startRoom?.id) {
      // Same room - warning
      this.renderer.renderHexHighlight(ctx, endHex, this.layout, "rgba(200, 170, 100, 0.4)");
    } else {
      // End not in a room - show warning
      this.renderer.renderHexHighlight(ctx, endHex, this.layout, "rgba(200, 100, 100, 0.4)");
    }
  }

  /**
   * Render a preview of subdivision stroke (bisect line or carve shape)
   */
  private renderSubdivisionPreview(strokePoints: import("./types.ts").StrokePoint[]): void {
    const previewLayer = this.layers.getLayer("preview");
    if (!previewLayer) return;

    const selectedRoomId = this.selection.getSelectedRoom();
    if (!selectedRoomId) return;

    const room = this.dungeon.rooms.get(selectedRoomId);
    if (!room) return;

    this.layers.clearLayer("preview");
    const ctx = previewLayer.ctx;

    // Draw the stroke itself
    const points: Point[] = strokePoints.map(p => ({ x: p.x, y: p.y }));
    this.renderer.renderStrokePreview(ctx, points, "rgba(255, 100, 100, 0.8)");

    // Check if this looks like a closed shape
    const stroke: Stroke = { points: strokePoints, isClosed: false };
    const closeness = getCloseness(stroke);

    if (strokePoints.length >= 8 && closeness > 0.3) {
      // Closed shape - show carve preview
      const processedStroke = closeness > 0.5 ? autoCloseStroke(stroke, 0.3) : stroke;
      const carveHexes = strokeToHexRegion(processedStroke, this.layout);

      // Show only hexes that intersect with the selected room
      const intersectionHexes = new Set<string>();
      for (const hexKey of carveHexes) {
        if (room.hexes.has(hexKey)) {
          intersectionHexes.add(hexKey);
        }
      }

      if (intersectionHexes.size > 0) {
        // Show carve preview in red/orange
        this.renderer.renderHexesPreview(ctx, intersectionHexes, this.layout, "rgba(255, 100, 50, 0.4)");

        // Show label
        const center = this.getRoomCenterFromHexes(intersectionHexes);
        ctx.save();
        ctx.font = "bold 12px system-ui, sans-serif";
        ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
        ctx.textAlign = "center";
        ctx.fillText(`CARVE (${intersectionHexes.size} hexes)`, center.x, center.y - 20);
        ctx.restore();
      }
    } else if (strokePoints.length >= 2) {
      // Line stroke - show bisect preview
      const lineStart = strokePoints[0];
      const lineEnd = strokePoints[strokePoints.length - 1];

      // Calculate which hexes would be on each side
      const dx = lineEnd.x - lineStart.x;
      const dy = lineEnd.y - lineStart.y;
      const a = -dy;
      const b = dx;
      const c = -(a * lineStart.x + b * lineStart.y);

      const sideA = new Set<string>();
      const sideB = new Set<string>();

      for (const hexKey of room.hexes) {
        const [q, r] = hexKey.split(",").map(Number);
        const center = hexToPixel({ q, r }, this.layout);
        const signedDist = a * center.x + b * center.y + c;

        if (signedDist >= 0) {
          sideA.add(hexKey);
        } else {
          sideB.add(hexKey);
        }
      }

      // Show the two sides in different colors
      if (sideA.size > 0 && sideB.size > 0) {
        this.renderer.renderHexesPreview(ctx, sideA, this.layout, "rgba(100, 200, 255, 0.3)");
        this.renderer.renderHexesPreview(ctx, sideB, this.layout, "rgba(255, 200, 100, 0.3)");

        // Draw the extended bisect line across the room
        ctx.save();
        ctx.strokeStyle = "rgba(255, 50, 50, 0.8)";
        ctx.lineWidth = 3;
        ctx.setLineDash([8, 4]);

        // Extend line to canvas edges
        const len = Math.hypot(dx, dy);
        if (len > 0) {
          const dirX = dx / len;
          const dirY = dy / len;
          const extendDist = 2000; // Extend far enough to cross any room

          ctx.beginPath();
          ctx.moveTo(lineStart.x - dirX * extendDist, lineStart.y - dirY * extendDist);
          ctx.lineTo(lineEnd.x + dirX * extendDist, lineEnd.y + dirY * extendDist);
          ctx.stroke();
        }

        ctx.setLineDash([]);
        ctx.restore();

        // Show label
        const midX = (lineStart.x + lineEnd.x) / 2;
        const midY = (lineStart.y + lineEnd.y) / 2;
        ctx.save();
        ctx.font = "bold 12px system-ui, sans-serif";
        ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
        ctx.textAlign = "center";
        ctx.fillText(`BISECT (${sideA.size} | ${sideB.size})`, midX, midY - 25);
        ctx.restore();
      }
    }

    // Also highlight the selected room outline
    this.renderer.renderSelectionOutline(ctx, room.hexes, this.layout, "rgba(255, 100, 100, 0.6)");
  }

  private clearPreviewLayer(): void {
    this.layers.clearLayer("preview");
    // Desires and hover will be rendered by the animation loop
  }

  /**
   * Calculate the center point of a room in pixel coordinates
   */
  private getRoomCenter(room: Room): Point {
    if (room.hexes.size === 0) {
      return { x: 0, y: 0 };
    }

    let sumX = 0;
    let sumY = 0;

    for (const key of room.hexes) {
      const [q, r] = key.split(",").map(Number);
      const center = hexToPixel({ q, r }, this.layout);
      sumX += center.x;
      sumY += center.y;
    }

    return {
      x: sumX / room.hexes.size,
      y: sumY / room.hexes.size,
    };
  }

  /**
   * Calculate the center point of a set of hexes in pixel coordinates
   */
  private getRoomCenterFromHexes(hexes: Set<string>): Point {
    let sumX = 0, sumY = 0;
    for (const key of hexes) {
      const [q, r] = key.split(",").map(Number);
      const center = hexToPixel({ q, r }, this.layout);
      sumX += center.x;
      sumY += center.y;
    }
    return { x: sumX / hexes.size, y: sumY / hexes.size };
  }

  /**
   * Render room drag preview showing the room(s) at their new position
   */
  private renderRoomDragPreview(): void {
    if (this.draggingRooms.length === 0 || !this.dragStartHex || !this.dragCurrentHex) return;

    const previewLayer = this.layers.getLayer("preview");
    if (!previewLayer) return;

    // Clear preview layer
    this.layers.clearLayer("preview");

    // Calculate the delta in hex coordinates
    const deltaQ = this.dragCurrentHex.q - this.dragStartHex.q;
    const deltaR = this.dragCurrentHex.r - this.dragStartHex.r;

    // Get IDs of all rooms being dragged
    const draggingRoomIds = new Set(this.draggingRooms.map(r => r.id));

    // Calculate new hex positions for all rooms being dragged
    const allPreviewHexes = new Set<string>();
    const allOriginalHexes = new Set<string>();

    for (const room of this.draggingRooms) {
      for (const hexKey of room.hexes) {
        const [q, r] = hexKey.split(",").map(Number);
        const newKey = `${q + deltaQ},${r + deltaR}`;
        allPreviewHexes.add(newKey);
        allOriginalHexes.add(hexKey);
      }
    }

    // Check for collisions with other rooms (not with rooms being dragged together)
    let hasCollision = false;
    for (const newKey of allPreviewHexes) {
      const existingRoom = this.dungeon.getRoom(newKey);
      if (existingRoom && !draggingRoomIds.has(existingRoom.id)) {
        hasCollision = true;
        break;
      }
    }

    // Render preview with appropriate color
    const color = hasCollision
      ? "rgba(200, 80, 80, 0.4)"   // Red for collision
      : "rgba(100, 200, 100, 0.4)"; // Green for valid

    this.renderer.renderHexesPreview(previewLayer.ctx, allPreviewHexes, this.layout, color);

    // Also render the original positions as ghost
    this.renderer.renderHexesPreview(
      previewLayer.ctx,
      allOriginalHexes,
      this.layout,
      "rgba(100, 100, 100, 0.2)"
    );

    // Show move delta indicator
    if (deltaQ !== 0 || deltaR !== 0) {
      const center = this.getRoomCenterFromHexes(allPreviewHexes);
      previewLayer.ctx.save();
      previewLayer.ctx.font = "bold 12px system-ui, sans-serif";
      previewLayer.ctx.fillStyle = hasCollision ? "rgba(255, 100, 100, 0.9)" : "rgba(255, 255, 255, 0.9)";
      previewLayer.ctx.textAlign = "center";
      const roomCount = this.draggingRooms.length;
      const label = hasCollision
        ? "collision!"
        : roomCount > 1
          ? `moving ${roomCount} rooms (${deltaQ}, ${deltaR})`
          : `moving (${deltaQ}, ${deltaR})`;
      previewLayer.ctx.fillText(label, center.x, center.y - 30);
      previewLayer.ctx.restore();
    }
  }

  /**
   * Commit moving multiple rooms and recalculate affected corridors
   */
  private commitRoomsMove(rooms: Room[], deltaQ: number, deltaR: number): void {
    const roomIds = new Set(rooms.map(r => r.id));

    // Categorize corridors:
    // 1. Internal corridors (both ends are rooms being moved - these just move with the rooms)
    // 2. External corridors (one end is being moved, one isn't - need recalculation)
    // 3. Pass-through corridors (pass through moved rooms but connect non-moved rooms)
    const internalCorridors = new Set<string>();
    const externalCorridors = new Set<string>();
    const passThroughCorridors = new Set<string>();

    // Get all hexes being moved
    const allMovingHexes = new Set<string>();
    for (const room of rooms) {
      for (const hexKey of room.hexes) {
        allMovingHexes.add(hexKey);
      }
    }

    // Categorize each corridor
    for (const corridor of this.dungeon.corridors.values()) {
      const [roomAId, roomBId] = corridor.connects;
      const roomAMoving = roomIds.has(roomAId);
      const roomBMoving = roomIds.has(roomBId);

      if (roomAMoving && roomBMoving) {
        // Both rooms moving together - internal corridor
        internalCorridors.add(corridor.id);
      } else if (roomAMoving || roomBMoving) {
        // Only one room moving - external corridor needs recalculation
        externalCorridors.add(corridor.id);
      } else {
        // Neither room is moving - check if corridor passes through any moving room
        for (const hexKey of corridor.hexes) {
          if (allMovingHexes.has(hexKey)) {
            passThroughCorridors.add(corridor.id);
            break;
          }
        }
      }
    }

    // Capture corridor state BEFORE move for undo
    const affectedCorridorIds = new Set([...internalCorridors, ...externalCorridors, ...passThroughCorridors]);
    const corridorsBefore = Array.from(affectedCorridorIds)
      .map(id => this.dungeon.corridors.get(id))
      .filter((c): c is Corridor => c !== undefined)
      .map(serializeCorridor);

    // Capture room state BEFORE move for diff visualization
    const roomsBefore = rooms.map(r => serializeRoom(r));

    // Move all rooms - check for collisions first
    // Pass all roomIds to exclude from collision checking (they're moving together)
    const movedRooms: Room[] = [];
    for (const room of rooms) {
      const movedRoom = this.dungeon.moveRoom(room.id, deltaQ, deltaR, roomIds);
      if (!movedRoom) {
        // Move failed (collision with non-moving room) - rollback previous moves
        for (let i = movedRooms.length - 1; i >= 0; i--) {
          this.dungeon.moveRoom(movedRooms[i].id, -deltaQ, -deltaR, roomIds);
        }
        this.strokeFeedback.showResult("can't move there", "error", this.lastStrokeEndPosition);
        this.clearPreviewLayer();
        return;
      }
      movedRooms.push(movedRoom);
    }

    // Update internal corridors - just shift their hex coordinates
    for (const corridorId of internalCorridors) {
      const corridor = this.dungeon.corridors.get(corridorId);
      if (!corridor) continue;

      const newHexes = corridor.hexes.map(hexKey => {
        const [q, r] = hexKey.split(",").map(Number);
        return `${q + deltaQ},${r + deltaR}`;
      });
      this.dungeon.updateCorridorPath(corridor.id, newHexes);
    }

    // Recalculate external corridors (one room moved, one didn't)
    for (const corridorId of externalCorridors) {
      const corridor = this.dungeon.corridors.get(corridorId);
      if (!corridor) continue;

      const roomA = this.dungeon.getRoomById(corridor.connects[0]);
      const roomB = this.dungeon.getRoomById(corridor.connects[1]);
      if (!roomA || !roomB) continue;

      const newPath = recalculateCorridorPath(roomA, roomB, this.layout);
      this.dungeon.updateCorridorPath(corridor.id, newPath);
    }

    // Recalculate pass-through corridors
    for (const corridorId of passThroughCorridors) {
      const corridor = this.dungeon.corridors.get(corridorId);
      if (!corridor) continue;

      const roomA = this.dungeon.getRoomById(corridor.connects[0]);
      const roomB = this.dungeon.getRoomById(corridor.connects[1]);
      if (!roomA || !roomB) continue;

      // Route through the moved rooms to maintain the pass-through relationship
      const newPath = recalculateCorridorPathThroughRooms(roomA, roomB, movedRooms, this.layout);
      this.dungeon.updateCorridorPath(corridor.id, newPath);
    }

    // Capture corridor state AFTER move for redo
    const corridorsAfter = Array.from(affectedCorridorIds)
      .map(id => this.dungeon.corridors.get(id))
      .filter((c): c is Corridor => c !== undefined)
      .map(serializeCorridor);

    // Record single undo operation for entire move
    const delta: OperationDelta = {
      type: "moveRooms",
      roomIds: movedRooms.map(r => r.id),
      deltaQ,
      deltaR,
      roomsBefore,
      corridorsBefore,
      corridorsAfter,
    };
    const roomCount = movedRooms.length;
    this.undoManager.recordOperation(
      "moveRooms",
      roomCount > 1 ? `Move ${roomCount} rooms` : `Move room`,
      delta
    );

    // Show success feedback
    const totalCorridors = internalCorridors.size + externalCorridors.size + passThroughCorridors.size;
    if (totalCorridors > 0) {
      this.strokeFeedback.showResult(
        roomCount > 1
          ? `moved ${roomCount} rooms, ${totalCorridors} corridor${totalCorridors > 1 ? 's' : ''} updated`
          : `moved, ${totalCorridors} corridor${totalCorridors > 1 ? 's' : ''} updated`,
        "success",
        this.lastStrokeEndPosition
      );
    } else {
      this.strokeFeedback.showResult(
        roomCount > 1 ? `moved ${roomCount} rooms` : "room moved",
        "success",
        this.lastStrokeEndPosition
      );
    }

    // Re-render everything
    this.clearPreviewLayer();
    this.renderRooms();
    this.updateDesires();
  }

  /**
   * Handle annotation confirmation - update room label
   */
  private handleAnnotationConfirm(roomId: string, label: string): void {
    const room = this.dungeon.rooms.get(roomId);
    if (!room) return;

    // Capture old label for undo
    const oldLabel = room.label;

    // Update room label
    room.label = label;
    console.log(`Room ${roomId} labeled: "${label}"`);

    // Record undo
    const delta: OperationDelta = {
      type: "labelRoom",
      roomId,
      oldLabel,
      newLabel: label,
    };
    this.undoManager.recordOperation("labelRoom", `Label: "${label}"`, delta);

    // Re-render to show label
    this.renderRooms();

    // Update desires after labeling
    this.updateDesires();
  }
}

// Initialize the app when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new HexcrawlApp("app");
});
